import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import ReactHtmlParser from 'react-html-parser';
import { Box, Grid, IconButton } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { Main } from '@klp/layout';
import { sanitizeHtmlContent } from '@forvalter/utils/sanitizeHtmlContent';
import {
    Heading,
    Block,
    Tooltip,
    Typography,
    ActionButton,
    ActionLink,
    IconName,
    Illustration,
    Loader,
} from '@klp/design';
import { useGetProsjektConfluenceContentQuery } from '../services/rtkApi';
import { ProsjektProsessDokumentasjonId, ProsjektRoutes } from '../config';

export default function ProsessDefinisjonDokumentasjon() {
    const [zIndex, setZIndex] = useState(0);
    const history = useHistory();
    const useStyles = makeStyles((theme) => ({
        datePicker: {
            width: '150px',
        },
        errorContainer: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            flexDirection: 'column',
        },
        errorContent: {
            display: 'flex',
            justifyContent: 'center',
            flexDirection: 'column',
            gap: theme.spacing(1),
        },
        actionButtons: {
            position: 'sticky',
            top: '10px',
            backgroundColor: 'white',
            zIndex: zIndex,
            width: 'fit-content',
        },
    }));

    const classes = useStyles();

    const {
        data: confluenceContentData,
        isError: confluenceContentError,
        isLoading: confluenceContentLoading,
        isFetching: confluenceContentFetching,
    } = useGetProsjektConfluenceContentQuery({
        id: ProsjektProsessDokumentasjonId(),
    });

    const handleScroll = () => {
        if (window.scrollY > 135) {
            setZIndex(1500);
        } else {
            setZIndex(0);
        }
    };

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    return (
        <Main>
            <Main.Breadcrumbs>
                <Main.Crumb
                    id="prosjektSchema"
                    label="breadcrumbs.project.schema"
                    href={ProsjektRoutes.projectSchema()}
                />
                <Main.Crumb
                    id="prosjektDokumentasjon"
                    label="project.header.documentation"
                    href={ProsjektRoutes.projectSchemaDocumentationUrl()}
                />
            </Main.Breadcrumbs>
            <Main.Navigation>
                <ActionLink
                    actionType="linkBack"
                    translation="project.backButton"
                    href={ProsjektRoutes.projectSchema()}
                />
            </Main.Navigation>
            {!confluenceContentError ? (
                <Loader
                    isLoading={
                        confluenceContentLoading || confluenceContentFetching
                    }
                >
                    {confluenceContentData?.body && (
                        <>
                            <Heading
                                klpHeadingLevel="ONE"
                                klpColor="dark"
                                translation="prosjekt.processDefinition.documentationTitle"
                            />
                            <Block
                                className={classes.actionButtons}
                                paddingTop="tiny"
                                paddingBottom="tiny"
                            >
                                <Grid
                                    container={true}
                                    direction="row"
                                    justifyContent="flex-start"
                                    spacing={1}
                                >
                                    <Grid item={true}>
                                        <Block>
                                            <Tooltip
                                                translation="prosjekt.processDefinition.read"
                                                title=""
                                            >
                                                <IconButton
                                                    color="inherit"
                                                    aria-label="Open external confluence view"
                                                    onClick={() =>
                                                        window.open(
                                                            confluenceContentData?.readLink,
                                                            '_blank',
                                                        )
                                                    }
                                                >
                                                    <Illustration
                                                        icon={IconName.EXTERNAL}
                                                    />
                                                </IconButton>
                                            </Tooltip>
                                        </Block>
                                    </Grid>
                                    <Grid item={true}>
                                        <Tooltip
                                            translation="prosjekt.processDefinition.iconTitle"
                                            title=""
                                        >
                                            <IconButton
                                                color="inherit"
                                                aria-label="Open detailed diagram view"
                                                onClick={() =>
                                                    history.push(
                                                        ProsjektRoutes.processdefinitionDiagram(),
                                                    )
                                                }
                                            >
                                                <Illustration
                                                    icon={IconName.CIRCLE_INFO}
                                                />
                                            </IconButton>
                                        </Tooltip>
                                    </Grid>
                                </Grid>
                            </Block>
                            <Block paddingTop="tiny">
                                {ReactHtmlParser(
                                    sanitizeHtmlContent(
                                        confluenceContentData.body,
                                    ),
                                )}
                            </Block>
                        </>
                    )}
                </Loader>
            ) : (
                <Block marginTop="fat" className={classes.errorContainer}>
                    <Box className={classes.errorContent}>
                        <Typography
                            variant="h5"
                            translation="general.error.message"
                        />

                        <ActionButton
                            actionType="button"
                            translation="general.reload"
                            onClick={() => window.location.reload()}
                        />
                    </Box>
                </Block>
            )}
        </Main>
    );
}
